<script lang="ts">
  import type { Area } from '../../../shared/contract'
  import Groomed from './Groomed.svelte'
  import Lamp from './Lamp.svelte'
  import RunGroups from './RunGroups.svelte'
  import Sign from './Sign.svelte'
  import StatusSign from './StatusSign.svelte'
  import {
    GRADE_NAME,
    byGrade,
    count,
    dead,
    exceptions,
    filtering,
    liftRows,
    matches,
    runState,
    sortRuns,
    turning,
    type Filter,
  } from '../tally'

  /**
   * One fabricated area sign: its lifts named with lamps, then one tally line,
   * then the few exceptions named. Folds to a 44 px line when another area is
   * open, when nothing here matches the filter, or when nothing here runs.
   * Opened, it lists every run by grade; that list is the only thing that scrolls.
   */
  let {
    area,
    anyOpen,
    filter,
    desk = false,
    open = false,
    folded = false,
    selected = false,
    named = false,
    withLifts = true,
    holdRows = true,
    shown,
    excShown,
    brief = false,
    ontoggle,
  }: {
    area: Area
    anyOpen: boolean
    filter: Filter
    /** Desktop shows compact area tabs; the board carries the lifts and runs. */
    desk?: boolean
    open?: boolean
    /** Another area is open on a phone. */
    folded?: boolean
    /** The area the desktop board is focused on. */
    selected?: boolean
    /** Early season: the one area with anything open names its open runs. */
    named?: boolean
    /** Lift names stay only while everything fits; then the lifts fold to lamps. */
    withLifts?: boolean
    /** A held lift takes a whole row for its word, while there is room for that. */
    holdRows?: boolean
    /** How many named runs fit; the rest become "+N more". */
    shown?: number
    /** How many exceptions fit on their one line; the rest become "+N". */
    excShown?: number
    /** A crowded day: this plate's exceptions line gives way (its tally still counts them). */
    brief?: boolean
    ontoggle: (name: string) => void
  } = $props()

  const f = $derived(filtering(filter) && !desk)
  const isDead = $derived(dead(area, anyOpen))
  const hits = $derived(sortRuns(area.runs.filter((r) => matches(r, filter))))
  const mini = $derived(!open && (folded || (f && !hits.length) || isDead))
  const runs = $derived(count(area.runs))
  const groomed = $derived(area.runs.filter((r) => r.groomed).length)
  const rows = $derived(liftRows(area.lifts))
  const turningLifts = $derived(area.lifts.filter(turning).length)
  const grades = $derived(byGrade(area.runs))
  const exc = $derived(brief ? null : exceptions(area, anyOpen))
  const excVisible = $derived(exc && excShown != null ? exc.runs.slice(0, excShown) : exc?.runs)
  /** The runs named inline: matches under a filter, or the open runs early in the season. */
  const named$ = $derived(
    f && !open
      ? hits
      : named && !f && !open
        ? sortRuns(area.runs.filter((r) => r.open))
        : null,
  )
  const visible = $derived(named$ && shown != null ? named$.slice(0, shown) : named$)
  const lampsOnly = $derived(!withLifts && !open)
  const label = $derived(
    `${area.name}: ${
      f
        ? `${hits.length || 'no'} matching run${hits.length === 1 ? '' : 's'}`
        : anyOpen
          ? `${runs.open} of ${runs.total} runs open`
          : `${runs.total} runs`
    }, ` +
      (area.lifts.length
        ? `${turningLifts} of ${area.lifts.length} lift${area.lifts.length > 1 ? 's' : ''} turning`
        : 'no lifts'),
  )
  /*
   * Lifts in two columns, in the report's order. While there is room, a held
   * lift takes a whole row so its word never crowds a name; so does a lift
   * left alone in a row. Long names also use the full width.
   */
  const fullRow = (lift: { name: string; status?: string } | undefined) => !!lift && (lift.name.length > 24 || (holdRows && !!lift.status))
  const wide = $derived.by(() => {
    let col = 0
    return rows.map((lift, i) => {
      const next = rows[i + 1]
      if (fullRow(lift) || (col === 0 && (!next || fullRow(next)))) {
        col = 0
        return true
      }
      col = 1 - col
      return false
    })
  })
</script>

<div
  class="plate"
  class:mini
  class:open
  class:nolift={!area.lifts.length}
  class:dead={isDead}
  class:sel={selected}
  class:named={named && !f && !open}
  class:filtering={f}
  class:lamps-only={lampsOnly}
  class:desk
>
  <div class="top">
    {#if !open}
    <button
      class="ph"
      data-area={area.name}
      aria-expanded={desk ? undefined : open}
      aria-pressed={desk ? selected : undefined}
      aria-controls={desk ? 'board' : undefined}
      aria-label={label}
      onclick={() => ontoggle(area.name)}
    >
      <span class="area-mark" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none"><path d="M4 3h16M14 3v6l-5 4v6h10M6 20h14M8 13h9v5M5 21l3-3" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
      <span class="lbl mono">{area.name}</span>
      {#if area.lifts.length && !(desk && isDead)}
        <span class="dots" aria-hidden="true">
          {#each area.lifts as lift}<Lamp on={turning(lift)} small />{/each}
        </span>
      {/if}
      <span class="rt">
        <span class="lc mono">
          {#if area.lifts.length}
            <b>{turningLifts}</b>/{area.lifts.length}<span class="lift-word"> lift{area.lifts.length > 1 ? 's' : ''}</span>
          {:else}
            no lifts
          {/if}
        </span>
        <span class="rc">
          {#if f}
            {#if hits.length}<b>{hits.length}</b> match{:else}no match{/if}
          {:else if isDead}
            <StatusSign state="closed" compact /><span class="closed-count">{runs.total} runs</span>
          {:else if anyOpen}
            <span><b>{runs.open}</b>/{runs.total} runs</span>
          {:else}
            {runs.total} runs
          {/if}
          {#if !desk}<i class="chev"></i>{/if}
        </span>
      </span>
    </button>
    {/if}
    {#if area.lifts.length}
      <ul class="lifts">
        {#each rows as lift, i}
          <li class="lift" class:on={lift.turning} class:wide={wide[i]} aria-label={lift.label} title={lift.label}>
            <Lamp on={lift.turning} />
            <span class="name">{lift.name}</span>
            {#if lift.status}<span class="hold mono">{lift.status}</span>{/if}
          </li>
        {/each}
      </ul>
    {/if}
  </div>

  {#if !open}
    <div class="tally">
      {#each grades as g (g.grade)}
        {#if anyOpen}
          <span class="t" class:z={!g.open}>
            <Sign grade={g.grade} off={!g.open} /><span aria-hidden="true"
              ><b>{g.open}</b>/{g.total}</span
            ><span class="vh">{GRADE_NAME[g.grade]}: {g.open} of {g.total} open</span>
          </span>
        {:else}
          <span class="t">
            <Sign grade={g.grade} off /><b aria-hidden="true">{g.total}</b><span class="vh"
              >{GRADE_NAME[g.grade]}: {g.total} runs</span
            >
          </span>
        {/if}
      {/each}
      {#if anyOpen && runs.open}
        <span class="t"
          ><Groomed /><b aria-hidden="true">{groomed}</b><span class="vh">{groomed} groomed</span
          ></span
        >
      {/if}
      <span class="go">
        {#if anyOpen}<span><b>{runs.open}</b>/{runs.total}</span>{:else}<span>{runs.total} runs</span>{/if}
        {#if !desk}<i class="chev"></i>{/if}
      </span>
    </div>
    {#if exc && excVisible}
      <div class="exc">
        <span class="mono">{exc.kind}</span>
        <span class="xs" data-exc={area.name}>
          {#each excVisible as run, i}
            {#if i}<span class="sep" aria-hidden="true">·</span>{/if}
            <span class="x" data-x
              ><Sign grade={run.grade} off={!run.open} />{run.name}<span class="vh"
                >, {runState(run)}</span
              ></span
            >
          {/each}
          {#if excVisible.length < exc.runs.length}
            {#if excVisible.length}<span class="sep" aria-hidden="true">·</span>{/if}
            <span class="x more">+{exc.runs.length - excVisible.length} <i class="chev"></i></span>
          {/if}
        </span>
      </div>
    {/if}
    {#if visible && !mini}
      <div class="hits" data-hits={area.name}>
        {#if !f}<span class="hit lab mono">Open</span>{/if}
        {#each visible as run}
          <span class="hit" class:off={!run.open} data-hit>
            <Sign grade={run.grade} off={!run.open} />{run.name}
            {#if run.groomed && filter.status !== 'groomed'}<Groomed />{/if}
            <span class="vh">, {runState(run)}</span>
          </span>
        {/each}
        {#if named$ && visible.length < named$.length}
          <span class="hit more">+{named$.length - visible.length} more <i class="chev"></i></span>
        {/if}
      </div>
    {/if}
  {:else}
    <!-- A scroller WebKit won't put in the tab order by itself. -->
    <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
    <div class="runs" role="region" aria-label="{area.name} runs" tabindex="0">
      <RunGroups runs={area.runs} {filter} {anyOpen} />
    </div>
  {/if}
</div>

<style>
  .plate {
    position: relative;
    flex: none;
    padding: 0 12px;
    background: var(--glass);
    border: 1px solid #ffffff;
    border-radius: 1px;
    box-shadow: 0 2px 0 #82949a, 0 5px 8px #142f3a26;
  }
  .plate::before {
    content: '';
    position: absolute;
    pointer-events: none;
    top: -3px;
    left: 2px;
    right: 2px;
    height: 3px;
    border-radius: 70% 30% 2px 1px;
    background: #f5f8ff;
    box-shadow: 0 1px 1px #c8d7ce66;
  }
  .ph::after { content: ''; position: absolute; inset: 0; border-radius: inherit; }
  .open .top { position: relative; }
  .ph {
    display: flex;
    align-items: center;
    gap: 7px;
    height: 34px;
    width: calc(100% + 24px);
    margin-inline: -12px;
    padding-right: 10px;
    text-align: left;
    background: #bc263c;
    color: #ffffff;
  }
  .ph:focus-visible { outline: none; }
  .ph:focus-visible::after { outline: 2px solid #439cc0; outline-offset: 2px; }
  .area-mark { display: flex; align-self: stretch; flex: none; align-items: center; justify-content: center; width: 29px; background: #ffffff; color: #bc263c; }
  .area-mark svg { width: 22px; height: 22px; }
  .lbl { flex: 1; min-width: 0; color: #ffffff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-size: 13px; font-weight: 750; text-transform: none; letter-spacing: .015em; }
  .rt { color: #ffffff; white-space: nowrap; display: flex; align-items: center; gap: 6px; }
  .rt b { color: #ffffff; font-weight: 600; }
  .lift-word, .dots, .rc { display: none; }
  .lc { font-size: 11px; letter-spacing: .02em; }
  .ph :global(.chev) { color: #ffffff; width: 9px; height: 9px; border-width: 2px; }
  .lifts { display: grid; grid-auto-rows: 20px; grid-template-columns: repeat(2, minmax(0, 1fr)); column-gap: 12px; margin-top: 3px; }
  .lift.wide { grid-column: 1 / -1; }
  .lift { display: flex; align-items: center; gap: 6px; min-width: 0; color: var(--ink-2); font-size: 13px; font-weight: 600; }
  .lift .name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; }
  .lift .hold { flex: 1 1 0; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--ink-2); letter-spacing: .03em; }
  .lift.on { color: var(--ink); }
  .tally { display: flex; align-items: center; gap: 9px; height: calc(var(--row) + 12px); border-top: 1px solid var(--line); margin-top: 5px; font-size: 14px; color: var(--ink-3); white-space: nowrap; }
  .nolift .tally { border-top: 0; margin-top: 0; }
  .t { display: flex; align-items: center; gap: 5px; }
  .t b { font-weight: 750; color: var(--ink); }
  .t.z b { color: var(--ink-3); }
  .go { margin-left: auto; display: flex; align-items: center; gap: 7px; color: var(--ink-3); }
  .go b { color: var(--ink); font-weight: 750; }
  .open .ph .chev { transform: rotate(-135deg); margin-top: 4px; }
  .exc { display: flex; align-items: baseline; gap: 8px; margin-top: -5px; padding-bottom: 8px; font-size: 13px; line-height: 18px; color: var(--ink-2); white-space: nowrap; overflow: hidden; }
  .exc .mono { color: var(--ink-3); flex: none; }
  .xs { display: flex; align-items: center; gap: 6px; min-width: 0; overflow: hidden; }
  .x { flex: none; display: inline-flex; align-items: center; gap: 6px; }
  .x.more { font: 500 11px/18px var(--font); letter-spacing: .05em; color: var(--ink-2); }
  .sep { color: var(--ink-3); }
  .mini { padding-top: 0; padding-bottom: 0; height: 44px; display: flex; flex-direction: column; justify-content: center; }
  .mini .lifts, .mini .tally, .mini .exc, .mini .lc { display: none; }
  .mini .ph { height: 42px; }
  .mini .rc, .filtering .rc { display: flex; align-items: center; gap: 7px; color: #ffffff; font-size: 13px; white-space: nowrap; }
  .rc :global(.mono) { color: #ffffff; }
  .filtering .tally, .filtering .exc { display: none; }
  .lamps-only .lifts, .filtering .lc { display: none; }
  .lamps-only .tally { border-top: 0; margin-top: 0; }
  .hits { display: flex; flex-wrap: wrap; column-gap: 14px; align-content: flex-start; overflow: hidden; margin-top: 4px; padding: 2px 0 7px; }
  .filtering:not(.lamps-only) .hits, .named .hits { border-top: 1px solid var(--line); margin-top: 6px; padding-top: 3px; }
  .named .hits { margin-top: 0; }
  .hit { display: inline-flex; align-items: center; gap: 6px; height: var(--row); font-size: 14px; white-space: nowrap; }
  .hit.off { color: var(--ink-3); }
  .hit.lab { color: var(--ink-3); font-size: 11px; }
  .hit.more { color: var(--ink-2); font-size: 11px; font-weight: 600; letter-spacing: .04em; text-transform: uppercase; }
  .open { flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column; }
  .open .top { flex: none; min-height: 0; }
  .open .lifts { padding-bottom: 3px; }
  .runs { flex: 1 1 auto; min-height: 0; overflow-y: auto; overscroll-behavior: contain; scrollbar-gutter: stable; margin: 0 -12px; padding: 0 12px 18px; border-top: 1px solid var(--line); mask-image: linear-gradient(#000 calc(100% - 12px), transparent); }
  @media (max-width: 999px) and (max-height: 760px) {
    .ph { height: 27px; gap: 6px; }
    .lifts { display: none; }
    .tally { border-top: 0; margin-top: 0; height: calc(var(--row) + 8px); }
    .open .top { min-height: 0; padding-bottom: 6px; }
    .open .ph::after { inset: -6px 0 -5px; }
  }
  @media (max-width: 400px) {
    .plate { padding-inline: 10px; }
    .ph { width: calc(100% + 20px); margin-inline: -10px; padding-right: 7px; gap: 6px; }
    .lbl { letter-spacing: 0; font-size: 12px; }
    .lift { font-size: 13px; gap: 6px; }
    .tally { gap: 7px; font-size: 13px; }
    .t { gap: 4px; }
    .runs { margin: 0 -10px; padding: 0 10px 18px; }
  }
  .desk { flex: 1 1 0; min-width: 0; height: 44px; padding: 0; box-shadow: none; }
  .desk::before { display: none; }
  .desk .lifts, .desk .tally, .desk .exc, .desk .hits, .desk .lc, .desk .closed-count { display: none; }
  .desk .ph { width: 100%; height: 42px; margin: 0; padding-right: 8px; background: #f7f9f8; }
  .desk .lbl { color: #263e4c; white-space: normal; font-size: 12px; line-height: 14px; overflow: visible; }
  .desk .area-mark { width: 24px; background: transparent; }
  .desk .area-mark svg { width: 19px; }
  .desk .rc { display: flex; align-items: center; gap: 4px; color: #49616f; font-size: 11px; }
  .desk .rt b { color: inherit; }
  .desk.sel .ph { background: #bc263c; }
  .desk.sel .lbl, .desk.sel .rc, .desk.sel .area-mark { color: #fff; }
  .sel { box-shadow: 0 0 0 1px #fff; }
  @media (min-width: 1200px) {
    .desk .lbl { font-size: 13px; }
  }
  @media (max-width: 999px), (max-height: 619px) {
    .ph { height: 27px; }
    .mini .ph { height: 42px; }
  }
</style>
